/**
 * The /comps page's rows as the collections its join code reads (RP-5, card
 * x9Qg-r89hVmW). GET /api/comps/rows answers one page of a kind, each row with
 * its allocations, properties, parcels, parties and contacts (api
 * src/data/comps-rows.ts states every list's rows, columns and order). The page
 * used to fetch those as capped reference lists and join them by id; this
 * flattens the page's rows into the same lists, so the page's join, filter,
 * sort, map and cart code runs unchanged over them.
 *
 * Pure (no imports): the API's parity test runs it against the old reads
 * (api tests/integration/comps-rows-parity.int.test.ts).
 */

type Row = Record<string, any>;

export interface CompsRow {
  transaction: Row;
  allocations: Row[];
  properties: Row[];
  parcels: Row[];
  parties: Row[];
  contacts: Row[];
}

export interface CompsRowsData {
  kind: 'sale' | 'lease';
  page: number;
  limit: number;
  /** Every transaction of the kind, as the /count route counted. */
  total: number;
  /** Transactions of the kind the sidebar filters match (= total with none set) — the page count (RP-5b). */
  matched: number;
  rows: CompsRow[];
  /** Non-null properties.trig_submarket_name, distinct (the sidebar's options). */
  submarkets: string[];
  /** Per property of the page's rows: allocations of each kind the viewer may see. */
  propertyDealCounts: Record<string, { sales: number; leases: number }>;
}

/** What the page used to get from its reference-set hooks, for the rows on the page. */
export interface CompsPageCollections {
  sales: Row[];
  leases: Row[];
  saleAllocs: Row[];
  leaseAllocs: Row[];
  saleParties: Row[];
  leaseParties: Row[];
  properties: Row[];
  contacts: Row[];
  parcels: Row[];
  submarkets: string[];
  propertyDealCounts: Record<string, { sales: number; leases: number }>;
}

function pushUnique(out: Row[], seen: Set<string>, rows: Row[]): void {
  for (const r of rows) {
    if (r?.id && !seen.has(r.id)) {
      seen.add(r.id);
      out.push(r);
    }
  }
}

/**
 * The page's sale and lease rows as flat lists: transactions in page order,
 * each transaction's allocations / parties in their stated order (the order the
 * page's per-transaction grouping keeps), properties / contacts / parcels once
 * each.
 */
export function flattenCompsRows(
  sale: CompsRowsData | null | undefined,
  lease: CompsRowsData | null | undefined,
): CompsPageCollections {
  const properties: Row[] = [];
  const contacts: Row[] = [];
  const parcels: Row[] = [];
  const seen = { properties: new Set<string>(), contacts: new Set<string>(), parcels: new Set<string>() };
  const side = (data: CompsRowsData | null | undefined) => {
    const rows = data?.rows ?? [];
    for (const r of rows) {
      pushUnique(properties, seen.properties, r.properties);
      pushUnique(contacts, seen.contacts, r.contacts);
      pushUnique(parcels, seen.parcels, r.parcels);
    }
    return {
      txns: rows.map((r) => r.transaction),
      allocs: rows.flatMap((r) => r.allocations),
      parties: rows.flatMap((r) => r.parties),
    };
  };
  const s = side(sale);
  const l = side(lease);
  return {
    sales: s.txns,
    leases: l.txns,
    saleAllocs: s.allocs,
    leaseAllocs: l.allocs,
    saleParties: s.parties,
    leaseParties: l.parties,
    properties,
    contacts,
    parcels,
    submarkets: [...new Set([...(sale?.submarkets ?? []), ...(lease?.submarkets ?? [])])],
    propertyDealCounts: { ...(lease?.propertyDealCounts ?? {}), ...(sale?.propertyDealCounts ?? {}) },
  };
}

/**
 * The map card's per-building totals: the server's counts (every allocation
 * the viewer may see) for the page's properties; for a property only a "Search
 * all DB" result brought in, the page's old count over the allocations it has
 * loaded.
 */
export function dealCountsByPropertyId(
  serverCounts: Record<string, { sales: number; leases: number }>,
  saleAllocs: Row[],
  leaseAllocs: Row[],
): Map<string, { sales: number; leases: number }> {
  const m = new Map<string, { sales: number; leases: number }>();
  for (const [pid, c] of Object.entries(serverCounts)) m.set(pid, { sales: c.sales, leases: c.leases });
  const loaded = new Map<string, { sales: number; leases: number }>();
  for (const [allocs, key] of [[saleAllocs, 'sales'], [leaseAllocs, 'leases']] as const) {
    for (const a of allocs) {
      const pid = a?.property_id;
      if (!pid || m.has(pid)) continue;
      const cur = loaded.get(pid) ?? { sales: 0, leases: 0 };
      cur[key] += 1;
      loaded.set(pid, cur);
    }
  }
  for (const [pid, c] of loaded) m.set(pid, c);
  return m;
}

/** The sidebar's submarket options: trimmed, non-empty, distinct, sorted (the page's old rule). */
export function submarketOptionsFrom(names: unknown[]): string[] {
  const set = new Set<string>();
  for (const sm of names) {
    if (typeof sm === 'string' && sm.trim()) set.add(sm.trim());
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** The sidebar filter fields the server applies (a structural subset of TransactionFilterValue; P its DatePreset). */
export interface CompsSidebarFilters<P extends string = string> {
  propertyTypeIds: string[];
  buildingClassIds: string[];
  addressQuery: string;
  parcelQuery: string;
  city: string;
  state: string;
  salePriceMin: string;
  salePriceMax: string;
  leaseRateMin: string;
  leaseRateMax: string;
  sqftMin: string;
  sqftMax: string;
  yearBuiltMin: string;
  yearBuiltMax: string;
  datePreset: P;
  dateMin: string;
  dateMax: string;
  colliersOnly: boolean;
  verifiedOnly: boolean;
  submarketNames: string[];
  capRateMin: string;
  capRateMax: string;
  landAcPriceMin: string;
  landAcPriceMax: string;
  leasedSfMin: string;
  leasedSfMax: string;
  effectiveRateMin: string;
  effectiveRateMax: string;
  freeRentMin: string;
  freeRentMax: string;
  tiAmountMin: string;
  tiAmountMax: string;
  termMonthsMin: string;
  termMonthsMax: string;
  leaseSubtypeIds: string[];
  rateUnit: string;
  commencementPreset: P;
  commencementMin: string;
  commencementMax: string;
  expirationPreset: P;
  expirationMin: string;
  expirationMax: string;
  permissionLevelIds: string[];
  internalExternal: string;
  keywords: string;
  partiesQuery: string;
  internalRefQuery: string;
}

/** The sidebar's preset → [from, to] resolver (TransactionFilterSidebar's resolveDateRange, passed in to keep this module import-free). */
export type ResolveDateRange<P extends string = string> = (value: { datePreset: P; dateMin: string; dateMax: string }) => { from: string | null; to: string | null };

/**
 * The applied sidebar filters as GET /api/comps/rows filter params (RP-5b) —
 * the normalisation the page's filter pass did before comparing, moved here so
 * the server compares the same values: needles trimmed + lower-cased (empty →
 * no filter); numbers through the page's `num` (unparseable → no bound); cap
 * rate % → fraction; lease / effective rate ×12 when typed per month; date
 * presets resolved here, in the browser, to from / to. Lists go one param per
 * value. Nothing set → no params. The predicates themselves: api
 * src/data/comps-rows-filters.ts.
 */
export function compsRowsFilterParams<P extends string>(filters: CompsSidebarFilters<P>, resolveDateRange: ResolveDateRange<P>): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  const text = (key: string, v: string) => {
    const needle = v.trim().toLowerCase();
    if (needle) out.push([key, needle]);
  };
  // Coerce range strings into numbers; empty string → undefined ("open ended").
  const num = (v: string) => {
    if (!v) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const range = (key: string, min: number | undefined, max: number | undefined) => {
    if (min !== undefined) out.push([`${key}Min`, String(min)]);
    if (max !== undefined) out.push([`${key}Max`, String(max)]);
  };
  const dates = (key: string, r: { from: string | null; to: string | null }) => {
    if (r.from) out.push([`${key}From`, r.from]);
    if (r.to) out.push([`${key}To`, r.to]);
  };
  const list = (key: string, values: string[]) => {
    for (const v of values) out.push([key, v]);
  };

  // Transaction.
  dates('date', resolveDateRange(filters));
  dates('commencement', resolveDateRange({ datePreset: filters.commencementPreset, dateMin: filters.commencementMin, dateMax: filters.commencementMax }));
  dates('expiration', resolveDateRange({ datePreset: filters.expirationPreset, dateMin: filters.expirationMin, dateMax: filters.expirationMax }));
  if (filters.internalExternal === 'internal' || filters.internalExternal === 'external') out.push(['internalExternal', filters.internalExternal]);
  if (filters.colliersOnly) out.push(['colliersOnly', 'true']);
  list('permissionLevelIds', filters.permissionLevelIds);
  list('leaseSubtypeIds', filters.leaseSubtypeIds);
  text('internalRef', filters.internalRefQuery);
  text('keywords', filters.keywords);
  text('parties', filters.partiesQuery);
  range('salePrice', num(filters.salePriceMin), num(filters.salePriceMax));
  // The cap_rate column stores the rate as a decimal fraction
  // (e.g. 0.065 = 6.5%). The UI accepts percent. Convert here.
  const capRateMinPct = num(filters.capRateMin);
  const capRateMaxPct = num(filters.capRateMax);
  range('capRate', capRateMinPct !== undefined ? capRateMinPct / 100 : undefined, capRateMaxPct !== undefined ? capRateMaxPct / 100 : undefined);
  range('landAcPrice', num(filters.landAcPriceMin), num(filters.landAcPriceMax));
  // Rate-unit conversion. Stored unit is $/SF/year; monthly input ×12.
  const rateMultiplier = filters.rateUnit === 'per_sf_per_month' ? 12 : 1;
  const annual = (v: number | undefined) => (v !== undefined ? v * rateMultiplier : undefined);
  range('leaseRate', annual(num(filters.leaseRateMin)), annual(num(filters.leaseRateMax)));
  range('effectiveRate', annual(num(filters.effectiveRateMin)), annual(num(filters.effectiveRateMax)));
  range('leasedSf', num(filters.leasedSfMin), num(filters.leasedSfMax));
  range('freeRent', num(filters.freeRentMin), num(filters.freeRentMax));
  range('tiAmount', num(filters.tiAmountMin), num(filters.tiAmountMax));
  range('termMonths', num(filters.termMonthsMin), num(filters.termMonthsMax));

  // Property (one allocated property meets them all).
  list('propertyTypeIds', filters.propertyTypeIds);
  list('buildingClassIds', filters.buildingClassIds);
  text('city', filters.city);
  text('state', filters.state);
  range('sqft', num(filters.sqftMin), num(filters.sqftMax));
  range('yearBuilt', num(filters.yearBuiltMin), num(filters.yearBuiltMax));
  if (filters.verifiedOnly) out.push(['verifiedOnly', 'true']);
  list('submarkets', filters.submarketNames);
  text('address', filters.addressQuery);
  text('parcel', filters.parcelQuery);
  return out;
}
